import React, { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { api } from '../../utils/api';

const SaleReceiptPrint = ({ sale, paymentMethod, paymentAmount, paymentDate }) => {
  // Funzione per formattare gli importi
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0.00';
    return parseFloat(amount || 0).toFixed(2);
  };

  const [receiptNumber, setReceiptNumber] = useState(null);

  // Funzione per generare un numero di ricevuta reale dal backend
  const generateRealReceiptNumber = async () => {
    try {
      const response = await api.get('/sales/generate-receipt-number');
      return response.data.receiptNumber;
    } catch (error) {
      console.error('Errore nella generazione del numero ricevuta:', error);
      // Fallback locale se il backend non risponde
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const counter = Math.floor(Date.now() / 1000) % 10000;
      return `${dateStr}-${counter.toString().padStart(4, '0')}`;
    }
  };

  // Determina il numero di ricevuta da mostrare
  const getReceiptNumber = () => {
    if (sale?.saleNumber) {
      return `${sale.saleNumber} (ID: ${sale.id})`; // Mostra sia il numero reale che l'ID
    } else if (sale?.id) {
      return `RIC-${sale.id}`; // Fallback al vecchio formato se disponibile
    } else if (receiptNumber) {
      return `${receiptNumber} (ID: da assegnare)`; // Mostra il numero generato con placeholder per ID
    } else {
      return 'Generando...'; // Mostra loading
    }
  };

  // Genera il numero di ricevuta quando il componente si monta
  useEffect(() => {
    if (!sale?.id && !sale?.saleNumber) {
      generateRealReceiptNumber().then(setReceiptNumber);
    }
  }, [sale]);

  // Calcola il totale dalla somma degli articoli
  const calculateTotal = () => {
    const items = sale?.SaleItems || sale?.items || [];
    return items.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price || item.unitPrice || 0);
      const quantity = parseInt(item.quantity || 0);
      return sum + (itemPrice * quantity);
    }, 0);
  };

  // Usa il totale calcolato dagli articoli
  const totalVendita = calculateTotal();
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const receiptRef = useRef();

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        setIsLoading(true);
        const companyId = localStorage.getItem('companyId');
        if (!companyId) {
          throw new Error('CompanyId not found in localStorage');
        }
        const response = await api.get('/company-settings', {
          headers: { 'x-company-id': companyId }
        });
        setCompanyData({
          name: response.data.companyName || 'FreeWheel',
          address: response.data.companyAddress || '',
          phone: response.data.companyPhone || '',
          city: response.data.companyCity || '',
          postalCode: response.data.companyPostalCode || '',
          province: response.data.companyProvince || '',
          email: response.data.companyEmail || '',
          vatNumber: response.data.companyVatNumber || ''
        });
      } catch (error) {
        console.error('Errore nel caricamento dei dati aziendali:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCompanySettings();
  }, []);

  // Stampa di debug per controllare i dati
  console.log('DEBUG SaleReceiptPrint - sale:', sale);
  console.log('DEBUG SaleReceiptPrint - paymentMethod:', paymentMethod);
  console.log('DEBUG SaleReceiptPrint - paymentAmount:', paymentAmount);
  console.log('DEBUG SaleReceiptPrint - paymentDate:', paymentDate);
  console.log('DEBUG SaleReceiptPrint - totalVendita calcolato:', totalVendita);

  // Conversione esplicita dei valori numerici
  const importoPagato = parseFloat(paymentAmount || sale?.paymentAmount || 0);
  const metodoPagamento = paymentMethod?.name || (sale?.PaymentMethod ? sale.PaymentMethod.name : (sale?.paymentMethodId ? 'Metodo ID: ' + sale.paymentMethodId : '-'));

  console.log('DEBUG SaleReceiptPrint - valori calcolati:', { 
    totalVendita, 
    importoPagato,
    metodoPagamento
  });

  // Assicuriamoci che formatCurrency gestisca correttamente i numeri
  console.log('DEBUG SaleReceiptPrint - totale formattato:', formatCurrency(totalVendita));

  // --- PAGINAZIONE ---
  const ROWS_PER_PAGE = 16;
  const items = sale?.SaleItems || sale?.items || [];
  const totalPages = Math.ceil(items.length / ROWS_PER_PAGE) || 1;
  const pagedItems = [];
  for (let i = 0; i < totalPages; i++) {
    pagedItems.push(items.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE));
  }

  if (isLoading) {
    return <div>Caricamento dati aziendali...</div>;
  }

  return (
    <div id="sale-receipt-print" ref={receiptRef} style={{
      width: '148mm',
      height: '210mm',
      margin: '0 auto',
      padding: '10mm',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontSize: '9pt',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      color: '#333',
      position: 'relative'
    }}>
      {pagedItems.map((pageItems, pageIndex) => (
        <div key={pageIndex} className="receipt-page" style={{
          width: '100%',
          height: '190mm',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          position: 'relative',
          marginBottom: pageIndex < totalPages - 1 ? '10mm' : 0
        }}>
          {/* Header con info aziendali e logo */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1.5mm',
            maxWidth: '100%'
          }}>
            <div style={{ maxWidth: '65%' }}>
              <div style={{ 
                fontSize: '14pt', 
                fontWeight: 'bold',
                color: '#DB1111',
                marginBottom: '1mm',
                wordBreak: 'break-word'
              }}>
                {companyData?.name}
              </div>
              <div style={{ 
                fontSize: '8pt', 
                color: '#666',
                lineHeight: '1.1'
              }}>
                <div style={{ wordBreak: 'break-word' }}>{companyData?.address}</div>
                <div>{companyData?.city} {companyData?.postalCode} {companyData?.province}</div>
                <div>Tel: {companyData?.phone}</div>
                <div>Email: {companyData?.email}</div>
                {companyData?.vatNumber && <div>P.IVA: {companyData?.vatNumber}</div>}
              </div>
            </div>
            <div style={{
              backgroundColor: '#f8f8f8',
              padding: '2mm',
              borderRadius: '2px',
              border: '1px solid #eee',
              minWidth: '40mm',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{ fontSize: '7pt', color: '#666' }}>
                Vendita N.:
              </div>
              <div style={{ fontSize: '12pt', fontWeight: 'bold', color: '#DB1111' }}>
                #{sale?.id}
              </div>
            </div>
          </div>

          {/* Titolo Documento */}
          <div style={{
            textAlign: 'center',
            fontSize: '13pt',
            fontWeight: 'bold',
            margin: '1.5mm 0',
            color: '#333',
            borderBottom: '2px solid #DB1111',
            paddingBottom: '0.5mm'
          }}>
            RICEVUTA DI VENDITA
          </div>

          {/* Info Cliente e Data */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5mm',
            marginBottom: '1.5mm',
            backgroundColor: '#f8f8f8',
            padding: '1.5mm',
            borderRadius: '2px'
          }}>
            <div>
              <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Cliente</div>
              <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{sale?.Customer ? `${sale.Customer.firstName} ${sale.Customer.lastName}` : ''}</div>
            </div>
            <div>
              <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Telefono</div>
              <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{sale?.Customer?.phone || ''}</div>
            </div>
            <div>
              <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Data Pagamento</div>
              <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>
                {paymentDate ? format(new Date(paymentDate), 'dd/MM/yyyy', { locale: it }) : format(new Date(), 'dd/MM/yyyy', { locale: it })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Numero Ricevuta</div>
              <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{getReceiptNumber()}</div>
            </div>
          </div>

          {/* Tabella prodotti */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', marginBottom: '20px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ccc' }}>
                <th style={{ textAlign: 'left', padding: '5px', fontSize: '11px' }}>Articolo</th>
                <th style={{ textAlign: 'center', padding: '5px', fontSize: '11px' }}>Quantità</th>
                <th style={{ textAlign: 'right', padding: '5px', fontSize: '11px' }}>Prezzo unitario</th>
                <th style={{ textAlign: 'right', padding: '5px', fontSize: '11px' }}>Totale</th>
              </tr>
            </thead>
            <tbody>
              {sale?.items?.map((item, index) => (
                <tr key={index} style={{ borderBottom: '1px dotted #eee' }}>
                  <td style={{ textAlign: 'left', padding: '5px', fontSize: '10px' }}>
                    {item.description}
                    {item.isCustom && (
                      <span style={{ 
                        backgroundColor: '#e3f2fd', 
                        color: '#2196f3', 
                        fontSize: '8px', 
                        padding: '2px 4px', 
                        borderRadius: '3px', 
                        marginLeft: '5px' 
                      }}>
                        Personalizzato
                      </span>
                    )}
                    {item.includeLabor && (
                      <span style={{ 
                        backgroundColor: '#fce4ec', 
                        color: '#e91e63', 
                        fontSize: '8px', 
                        padding: '2px 4px', 
                        borderRadius: '3px', 
                        marginLeft: '5px' 
                      }}>
                        Con manodopera
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: '5px', fontSize: '10px' }}>{item.quantity}</td>
                  <td style={{ textAlign: 'right', padding: '5px', fontSize: '10px' }}>€{parseFloat(item.price).toFixed(2)}</td>
                  <td style={{ textAlign: 'right', padding: '5px', fontSize: '10px' }}>€{(item.quantity * item.price).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Dettagli Pagamento SOLO nell'ultima pagina */}
          {pageIndex === totalPages - 1 && (
            <div style={{ marginBottom: '3mm' }}>
              <div style={{ 
                fontWeight: 'bold',
                marginBottom: '0.5mm',
                color: '#DB1111',
                fontSize: '10pt'
              }}>
                DETTAGLI PAGAMENTO
              </div>
              <div style={{ 
                padding: '1.5mm',
                border: '1px solid #eee',
                borderRadius: '2px',
                backgroundColor: '#f8f8f8'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '0.5mm 1mm', borderBottom: '1px solid #eee' }}>Totale vendita</td>
                      <td style={{ padding: '0.5mm 1mm', textAlign: 'right', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                        €{formatCurrency(totalVendita)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.5mm 1mm', borderBottom: '1px solid #eee' }}>Importo pagato</td>
                      <td style={{ padding: '0.5mm 1mm', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        €{formatCurrency(importoPagato)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.5mm 1mm', borderBottom: '1px solid #eee' }}>Metodo di pagamento</td>
                      <td style={{ padding: '0.5mm 1mm', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        {metodoPagamento}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ flex: 1 }} />

          {/* Numero pagina in basso a destra, su tutte le pagine */}
          <div
            style={{
              position: 'absolute',
              bottom: '1mm',
              right: '10mm',
              fontSize: '8pt',
              color: '#888'
            }}
          >
            Pagina {pageIndex + 1} / {totalPages}
          </div>

          {/* Messaggio di ringraziamento in calce */}
          {pageIndex === totalPages - 1 && (
            <div style={{
              width: '100%',
              textAlign: 'center',
              marginTop: '5mm',
              fontSize: '10pt',
              color: '#333',
              fontWeight: 'bold'
            }}>
              Grazie per aver scelto i nostri servizi!<br />
              Lo staff di {companyData?.name}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SaleReceiptPrint; 